<template>
  <div class="checkout-page">
    <Steps step="checkout" />

    <h1 class="checkout-title">Confirma y envía</h1>
    <p class="checkout-subtitle">Revisa el desglose y confirma. Un clic para levantar todos los pedidos.</p>

    <!-- Error -->
    <div v-if="submitError" class="checkout-error">{{ submitError }}</div>

    <div class="checkout-grid">

      <!-- Left: per-comedor cards -->
      <div class="checkout-cards">
        <div
          v-for="{ cid, lines, total } in perComedor"
          :key="cid"
          class="comedor-card"
        >
          <!-- Card header -->
          <div class="comedor-card__header">
            <div>
              <div class="comedor-card__name-row">
                <h3 class="comedor-card__name">{{ store.getCenterById(cid)?.name || cid }}</h3>
                <Pill>{{ lines.length }} item{{ lines.length !== 1 ? 's' : '' }}</Pill>
              </div>
              <div class="comedor-card__address">{{ store.getCenterById(cid)?.address || '' }}</div>
            </div>
            <div class="comedor-card__total">{{ money(total) }}</div>
          </div>

          <!-- Product lines -->
          <div class="lines-table">
            <!-- Header -->
            <div class="lines-table__header">
              <span>Producto</span>
              <span class="text-right">Cant.</span>
              <span class="text-right">P. Unit.</span>
              <span class="text-right">Subtotal</span>
            </div>

            <!-- Rows -->
            <div
              v-for="line in lines"
              :key="line.pid"
              class="lines-table__row"
            >
              <span class="lines-table__product-name">{{ line.name }}</span>
              <span class="lines-table__qty">{{ line.qty }}</span>
              <span class="lines-table__price">{{ money(line.price) }}</span>
              <span class="lines-table__subtotal">{{ money(line.price * line.qty) }}</span>
            </div>
          </div>

        </div>
      </div>

      <!-- Right: sticky summary -->
      <div class="checkout-summary">
        <div class="summary-title">Resumen</div>

        <div class="summary-rows">
          <div
            v-for="{ cid, total, lines } in perComedor"
            :key="cid"
            class="summary-row"
          >
            <span class="summary-row__label">
              {{ store.getCenterById(cid)?.name || cid }}
              <span class="summary-row__count">({{ lines.length }})</span>
            </span>
            <span class="summary-row__amount">{{ money(total) }}</span>
          </div>
        </div>

        <div class="summary-grand">
          <span class="summary-grand__label">Total</span>
          <span class="summary-grand__amount">{{ money(grand) }}</span>
        </div>

        <button
          @click="handleConfirm"
          :disabled="submitting || perComedor.length === 0"
          class="btn-confirm"
          :class="{ 'btn-confirm--loading': submitting }"
        >
          <span v-if="submitting">Enviando…</span>
          <template v-else>
            <Icon name="check" :size="16" color="#fff" :stroke="2.5" /> Confirmar y enviar pedido
          </template>
        </button>

        <button @click="$emit('back')" class="btn-back">
          Volver al editor
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { createOrder } from '../services/orders.js'
import Steps from './ui/Steps.vue'
import Pill  from './ui/Pill.vue'
import Icon  from './ui/Icon.vue'

const props = defineProps({
  activeIds: { type: Array,  default: () => [] },
  cart:      { type: Object, default: () => ({}) },
  notes:     { type: Object, default: () => ({}) },
  store:     { type: Object, required: true },
  auth:      { type: Object, required: true },
})

const emit = defineEmits(['back', 'confirm'])

const submitting  = ref(false)
const submitError = ref('')

function nextDeliveryDate() {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  if (d.getDay() === 0) d.setDate(d.getDate() + 1)
  return d.toISOString()
}

function money(n) {
  return '$' + Number(n || 0).toLocaleString('es-MX', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })
}

const productMap = computed(() => {
  const map = {}
  for (const p of (props.store.products?.value || [])) {
    map[String(p.id)] = p
  }
  return map
})

function resolveProduct(pid) {
  const p = productMap.value[String(pid)]
  if (!p) console.warn('[Checkout] product not found for pid:', pid, '— store has', Object.keys(productMap.value).length, 'products')
  return p || null
}

const perComedor = computed(() =>
  props.activeIds
    .map(cid => {
      const lines = Object.entries(props.cart[cid] || {})
        .filter(([, q]) => q > 0)
        .map(([pid, qty]) => {
          const product = resolveProduct(pid)
          const price   = product ? product.price : 0
          const name    = product ? product.name  : String(pid)
          return { pid, qty, price, name }
        })
      const total = lines.reduce((s, l) => s + l.price * l.qty, 0)
      return { cid, lines, total }
    })
    .filter(p => p.lines.length > 0)
)

const grand = computed(() => perComedor.value.reduce((s, p) => s + p.total, 0))


async function handleConfirm() {
  if (submitting.value || perComedor.value.length === 0) return
  submitting.value = true
  submitError.value = ''

  try {
    const userId    = props.auth.userId?.value ?? props.auth.userId
    const orderedAt = new Date().toISOString()
    const companyId = props.auth.companyId?.value ?? props.auth.companyId

    const promises = perComedor.value.map(({ cid, lines, total }) => {
      const details = lines.map(({ pid, qty, price }) => ({
        product_id: pid,
        quantity:   qty,
        unit_price: price,
        list_price: price,
        total:      qty * price,
        discount:   0,
      }))
      const center = props.store.getCenterById(cid)
      return createOrder({
        center_id:         center?.center_id ?? null,
        client_id:         cid,
        route_id:          center?.route_id ?? null,
        route_delivery_id: center?.route_delivery_id ?? null,
        user_id:       userId,
        ordered_at:    orderedAt,
        delivery_date: nextDeliveryDate(),
        status_id:     1,
        order_type_id: 1,
        total,
        cash:          total,
        credit:        0,
        check:         0,
        notes:         '',
        details,
        visit: {
          client_id:  cid,
          user_id:    userId,
          route_id:   center?.route_id ?? null,
          visited_at: orderedAt,
          company_id: companyId,
          latitude:   center?.latitude  ?? 0,
          longitude:  center?.longitude ?? 0,
        },
      })
    })

    await Promise.all(promises)

    props.store.loadLastOrders().catch(() => {})

    emit('confirm')
  } catch (e) {
    submitError.value = e.message || 'Error al enviar el pedido'
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
/* ─── Page ───────────────────────────────────────────────────────────────────── */
.checkout-page {
  padding: 36px 48px;
  max-width: 1100px;
  margin: 0 auto;
}

.checkout-title {
  margin: 14px 0 6px;
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 28px;
  color: var(--text);
  letter-spacing: -0.3px;
}

.checkout-subtitle {
  margin: 0;
  font-family: var(--ff-body);
  font-size: 14px;
  color: var(--text-2);
}

.checkout-error {
  margin-top: 16px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-family: var(--ff-body);
  font-size: 13px;
  color: #dc2626;
}

/* ─── 2-column grid ──────────────────────────────────────────────────────────── */
.checkout-grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 24px;
  margin-top: 24px;
}

.checkout-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ─── Comedor card ───────────────────────────────────────────────────────────── */
.comedor-card {
  background: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--border);
  padding: 22px;
  box-shadow: var(--shadow);
}

.comedor-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 18px;
}

.comedor-card__name-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.comedor-card__name {
  margin: 0;
  font-family: var(--ff-head);
  font-size: 17px;
  font-weight: 700;
  color: var(--text);
}

.comedor-card__address {
  font-family: var(--ff-body);
  font-size: 12px;
  color: var(--text-2);
  margin-top: 4px;
}

.comedor-card__total {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 20px;
  color: var(--text);
}

/* ─── Lines table ────────────────────────────────────────────────────────────── */
.lines-table {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.lines-table__header {
  display: grid;
  grid-template-columns: 1fr 60px 90px 90px;
  padding: 4px 8px;
  font-family: var(--ff-body);
  font-size: 10px;
  color: var(--text-3);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 700;
}

.lines-table__row {
  display: grid;
  grid-template-columns: 1fr 60px 90px 90px;
  padding: 7px 8px;
  border-radius: 6px;
  background: var(--bg);
  font-family: var(--ff-body);
  font-size: 13px;
  align-items: center;
}

.lines-table__product-name {
  color: var(--text);
  font-weight: 600;
  line-height: 1.3;
}

.lines-table__qty {
  text-align: right;
  color: var(--text-2);
  font-family: var(--ff-head);
}

.lines-table__price {
  text-align: right;
  color: var(--text-2);
}

.lines-table__subtotal {
  text-align: right;
  color: var(--text);
  font-weight: 700;
  font-family: var(--ff-head);
}

/* ─── Summary sidebar ────────────────────────────────────────────────────────── */
.checkout-summary {
  background: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--border);
  padding: 22px;
  position: sticky;
  top: 24px;
  height: fit-content;
  box-shadow: var(--shadow);
}

.summary-title {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 14px;
  color: var(--text);
}

.summary-rows {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  font-family: var(--ff-body);
  font-size: 13px;
}

.summary-row__label {
  color: var(--text-2);
}

.summary-row__count {
  color: var(--text-3);
}

.summary-row__amount {
  color: var(--text);
  font-weight: 700;
}

.summary-grand {
  border-top: 1px solid var(--border);
  margin-top: 14px;
  padding-top: 14px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.summary-grand__label {
  font-family: var(--ff-body);
  font-size: 13px;
  color: var(--text-2);
}

.summary-grand__amount {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 26px;
  color: var(--text);
  letter-spacing: -0.5px;
}

/* ─── Buttons ────────────────────────────────────────────────────────────────── */
.btn-confirm {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 52px;
  padding: 0 22px;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: var(--ff-head);
  font-weight: 600;
  font-size: 15px;
  margin-top: 18px;
  transition: background 0.15s;
}

.btn-confirm--loading {
  background: var(--border-strong);
  cursor: not-allowed;
}

.btn-back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: var(--ff-head);
  font-weight: 600;
  font-size: 14px;
  color: var(--text-2);
  margin-top: 6px;
}

/* ─── Utility ────────────────────────────────────────────────────────────────── */
.text-right {
  text-align: right;
}
</style>
